import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { EmptyState } from './EmptyState';
import PlaceIcon from '@mui/icons-material/Place';

/**
 * An interactive pickup → drop map for one task, with the ninja's live
 * position while the job is in progress.
 *
 * Leaflet + OpenStreetMap rather than Google Maps: no API key to provision,
 * restrict or bill, and no risk of quietly reusing the mobile app's key
 * (which is very likely restricted to the app's own bundle id) on a surface
 * it was never scoped for. Leaflet is BSD-2-Clause; react-leaflet is
 * Hippocratic-2.1, which is unusual but imposes no constraint on ordinary
 * commercial logistics use — worth knowing, not a blocker.
 *
 * Coordinates are optional on every task point (see DeliveryTask in
 * adminService.ts) — older bookings and manually-entered addresses may not
 * have them. This renders whatever is available and says plainly when
 * nothing is, rather than showing a misleadingly empty map.
 */

export interface RoutePoint {
  lat: number;
  lng: number;
  label: string;
  /** Sub-line under the label — the address, typically. */
  detail?: string;
}

interface TaskRouteMapProps {
  pickup?: RoutePoint | null;
  drop?: RoutePoint | null;
  /** The assigned ninja's last known position, shown only while the job is in progress. */
  ninjaLocation?: { lat: number; lng: number } | null;
  height?: number;
}

/** A coloured circular pin rendered as a DivIcon — no external icon assets to
 *  bundle or theme, and it can carry the brand palette directly. */
function pinIcon(color: string, label: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `
      <div style="
        width: 30px; height: 30px; border-radius: 50% 50% 50% 0;
        background: ${color}; transform: rotate(-45deg);
        box-shadow: 0 2px 6px rgba(0,0,0,0.35);
        border: 2px solid #fff;
        display: flex; align-items: center; justify-content: center;
      ">
        <span style="
          transform: rotate(45deg); color: #fff; font-weight: 700;
          font-size: 12px; font-family: sans-serif;
        ">${label}</span>
      </div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });
}

function ninjaIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `
      <div style="
        width: 20px; height: 20px; border-radius: 50%;
        background: ${color}; border: 3px solid #fff;
        box-shadow: 0 0 0 3px ${color}55, 0 2px 6px rgba(0,0,0,0.4);
      "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

/**
 * Fits the viewport to whatever points are present, on mount and whenever the
 * set of points changes — a task detail is reused across rows, so this
 * cannot rely on running only once.
 *
 * Also re-invalidates the map's size on a short delay and on window resize.
 * Leaflet measures its container once at construction and caches that pixel
 * origin; it has no ResizeObserver of its own. This map always opens inside
 * a MUI Dialog that animates from a point to full size, so without this the
 * map initialises at the dialog's transient (smaller, sometimes zero-width)
 * size, and every marker after that is positioned against the wrong origin —
 * present in the DOM, just not where fitBounds actually put the viewport.
 */
function FitToPoints({ points }: { points: [number, number][] }) {
  const map = useMap();

  const fit = React.useCallback(() => {
    map.invalidateSize();
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
    } else {
      map.fitBounds(L.latLngBounds(points), { padding: [32, 32], maxZoom: 15 });
    }
  }, [map, JSON.stringify(points)]);

  React.useEffect(() => {
    fit();
    // Matches (with margin) MUI's default Dialog transition duration, so this
    // fires once the container has actually reached its final size.
    const settleTimer = setTimeout(fit, 300);
    window.addEventListener('resize', fit);
    return () => {
      clearTimeout(settleTimer);
      window.removeEventListener('resize', fit);
    };
  }, [fit]);

  return null;
}

export const TaskRouteMap: React.FC<TaskRouteMapProps> = ({
  pickup,
  drop,
  ninjaLocation,
  height = 320,
}) => {
  const theme = useTheme();

  const points = useMemo<[number, number][]>(() => {
    const pts: [number, number][] = [];
    if (pickup) pts.push([pickup.lat, pickup.lng]);
    if (drop) pts.push([drop.lat, drop.lng]);
    if (ninjaLocation) pts.push([ninjaLocation.lat, ninjaLocation.lng]);
    return pts;
  }, [pickup, drop, ninjaLocation]);

  if (points.length === 0) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <EmptyState
          icon={<PlaceIcon />}
          title="No coordinates on this booking"
          description="This task's addresses were saved without map coordinates, so the route can't be plotted. The address list below still has everything on file."
        />
      </Box>
    );
  }

  const center = points[0];

  return (
    <Box
      sx={{
        height,
        borderRadius: 2,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        // Leaflet ships its own z-index stack for controls; without this the
        // map's zoom buttons can float above a MUI dialog's own chrome.
        '& .leaflet-container': { zIndex: 0 },
      }}
    >
      <MapContainer
        center={center}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToPoints points={points} />

        {pickup && drop && (
          <Polyline
            positions={[
              [pickup.lat, pickup.lng],
              [drop.lat, drop.lng],
            ]}
            pathOptions={{
              color: theme.palette.primary.main,
              weight: 4,
              opacity: 0.85,
              dashArray: '1 10',
              lineCap: 'round',
            }}
          />
        )}

        {pickup && (
          <Marker position={[pickup.lat, pickup.lng]} icon={pinIcon(theme.palette.error.main, 'A')} />
        )}
        {drop && (
          <Marker position={[drop.lat, drop.lng]} icon={pinIcon(theme.palette.success.main, 'B')} />
        )}
        {ninjaLocation && (
          <Marker
            position={[ninjaLocation.lat, ninjaLocation.lng]}
            icon={ninjaIcon(theme.palette.info.main)}
          />
        )}
      </MapContainer>
    </Box>
  );
};

/** Small legend row to sit under the map — what each marker means. */
export const TaskRouteMapLegend: React.FC<{ showNinja?: boolean }> = ({ showNinja }) => {
  const dot = (color: string) => (
    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
  );
  return (
    <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {dot('error.main')}
        <Typography variant="caption" color="text.secondary">
          Pickup
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {dot('success.main')}
        <Typography variant="caption" color="text.secondary">
          Drop
        </Typography>
      </Box>
      {showNinja && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {dot('info.main')}
          <Typography variant="caption" color="text.secondary">
            Ninja's last known position
          </Typography>
        </Box>
      )}
    </Box>
  );
};
